import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../auth/AuthProvider.jsx'
import { fetchMovimentacoes, fetchProdutos } from '../data/syncManager.js'
import { listObras } from '../data/obras.js'
import BackButton from '../components/BackButton.jsx'
import Toast from '../components/Toast.jsx'
import { getFinanceSettings } from '../data/financeSettings.js'
import { listNotasFiscais } from '../data/notasFiscais.js'

function sum(arr) { return arr.reduce((a,b)=>a+(Number(b)||0),0) }

export default function FinanceiroPage() {
  const { session } = useAuth()
  const [movs, setMovs] = useState([])
  const [prods, setProds] = useState([])
  const [nfs, setNfs] = useState([])
  const [obras, setObras] = useState([])
  const [toast, setToast] = useState(null)
  const [toastType, setToastType] = useState('info')
  const [period, setPeriod] = useState({ from: '', to: '' })
  const [fin, setFin] = useState({ enabled: true, hideValues: false })
  const [showNfDetalhes, setShowNfDetalhes] = useState(false)

  const isAdmin = session?.perfil === 'admin'

  useEffect(() => {
    (async () => {
      try {
        const [m, p, fs, notasFiscais, obrasList] = await Promise.all([
          fetchMovimentacoes(),
          fetchProdutos(),
          getFinanceSettings(),
          listNotasFiscais({ status: 'ativa' }),
          listObras({ status: 'ativa' })
        ])
        setMovs(m || [])
        setProds(p || [])
        setFin(fs || { enabled: true, hideValues: false })
        setNfs(notasFiscais || [])
        setObras(obrasList || [])
      } catch (e) {
        setToastType('error'); setToast('Falha ao carregar dados do financeiro')
      }
    })()
  }, [])

  const filtered = useMemo(() => {
    let list = [...movs]
    const fromTs = period.from ? new Date(period.from).getTime() : null
    const toTs = period.to ? (new Date(period.to).getTime() + 86399999) : null
    if (fromTs) list = list.filter(m => (m.criadoEm || m.dataMovimentacao || 0) >= fromTs)
    if (toTs) list = list.filter(m => (m.criadoEm || m.dataMovimentacao || 0) <= toTs)
    return list
  }, [movs, period])

  const consumo = filtered.filter(m => m.classificacao === 'consumo')
  const prejuizo = filtered.filter(m => m.classificacao === 'prejuizo')
  const manutencao = filtered.filter(m => m.classificacao === 'manutencao')
  const investimento = filtered.filter(m => m.tipo === 'entrada' || m.classificacao === 'investimento')

  const total = (list) => sum(list.map(m => m.custoTotal ?? 0))

  const estoqueAtual = useMemo(() => {
    return sum(prods.map(p => (Number(p.quantidade)||0) * (Number(p.custoUnitario ?? p.dados?.custoUnitario)||0)))
  }, [prods])

  // Calcular gastos por obra
  const gastosPorObra = useMemo(() => {
    const mapa = {}
    filtered.forEach(m => {
      if (!m.obraId || !m.custoTotal) return
      if (!mapa[m.obraId]) {
        mapa[m.obraId] = { obraId: m.obraId, obraNome: m.obraNome || 'Obra não identificada', total: 0 }
      }
      mapa[m.obraId].total += Number(m.custoTotal) || 0
    })
    return Object.values(mapa).sort((a, b) => b.total - a.total)
  }, [filtered])

  const semCusto = useMemo(() => {
    return prods.filter(p => {
      const c = p.custoUnitario ?? p.dados?.custoUnitario
      return c == null || Number(c) === 0
    }).length
  }, [prods])

  const nfsFiltradas = useMemo(() => {
    let list = [...nfs]
    const fromTs = period.from ? new Date(period.from).getTime() : null
    const toTs = period.to ? (new Date(period.to).getTime() + 86399999) : null
    if (fromTs) list = list.filter(n => n.criadoEm >= fromTs)
    if (toTs) list = list.filter(n => n.criadoEm <= toTs)
    return list
  }, [nfs, period])

  const totalNfs = useMemo(() => sum(nfsFiltradas.map(n => n.valorTotal ?? 0)), [nfsFiltradas])
  const prodsComNf = useMemo(() => prods.filter(p => p.notaFiscalId).length, [prods])
  const prodsSemNf = useMemo(() => prods.filter(p => !p.notaFiscalId).length, [prods])

  const fornecedoresMap = useMemo(() => {
    const map = {}
    nfsFiltradas.forEach(n => {
      const key = n.fornecedorNome || 'Sem nome'
      if (!map[key]) map[key] = { total: 0, qtd: 0 }
      map[key].total += Number(n.valorTotal ?? 0)
      map[key].qtd += 1
    })
    return Object.entries(map).sort((a, b) => b[1].total - a[1].total)
  }, [nfsFiltradas])

  const money = (v) => fin.hideValues ? '—' : (Number(v||0)).toLocaleString('pt-BR',{style:'currency',currency:'BRL'})

  if (!isAdmin || !fin.enabled) {
    return (
      <div className="space-y-4">
        <BackButton />
        <div className="rounded-2xl cm-card p-4 text-sm text-white/80">Módulo Financeiro desativado ou sem permissão.</div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Toast message={toast} type={toastType} onClose={() => setToast(null)} />
      <BackButton />

      <div className="rounded-2xl cm-card p-4">
        <div className="text-sm font-semibold">Filtros</div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <input type="date" value={period.from} onChange={e=>setPeriod(p=>({...p,from:e.target.value}))} className="rounded-xl bg-white/10 px-3 py-2 text-sm outline-none" />
          <input type="date" value={period.to} onChange={e=>setPeriod(p=>({...p,to:e.target.value}))} className="rounded-xl bg-white/10 px-3 py-2 text-sm outline-none" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-2xl cm-card p-3 text-xs">
          <div className="text-white/60">Investimento</div>
          <div className="mt-1 text-lg font-semibold">{money(total(investimento))}</div>
        </div>
        <div className="rounded-2xl cm-card p-3 text-xs">
          <div className="text-white/60">Consumo</div>
          <div className="mt-1 text-lg font-semibold">{money(total(consumo))}</div>
        </div>
        <div className="rounded-2xl cm-card p-3 text-xs">
          <div className="text-white/60">Prejuízo</div>
          <div className="mt-1 text-lg font-semibold">{money(total(prejuizo))}</div>
        </div>
        <div className="rounded-2xl cm-card p-3 text-xs">
          <div className="text-white/60">Manutenção</div>
          <div className="mt-1 text-lg font-semibold">{money(total(manutencao))}</div>
        </div>
        <div className="rounded-2xl cm-card p-3 text-xs col-span-2">
          <div className="text-white/60">Estoque atual (valor)</div>
          <div className="mt-1 text-lg font-semibold">{money(estoqueAtual)}</div>
          {semCusto > 0 && (
            <div className="mt-2 text-yellow-400/80 flex items-center gap-1">
              <span>⚠️</span>
              <span>{semCusto} produto{semCusto > 1 ? 's' : ''} sem custo cadastrado — valor pode estar incompleto.</span>
            </div>
          )}
        </div>
      </div>

      {/* 📎 Seção Notas Fiscais */}
      <div className="rounded-2xl cm-card p-4">
        <button
          type="button"
          onClick={() => setShowNfDetalhes(s => !s)}
          className="flex w-full items-center justify-between"
        >
          <div className="text-sm font-semibold">📎 Compras (Notas Fiscais)</div>
          <span className="text-xs text-white/60">{showNfDetalhes ? 'Ocultar ▲' : 'Ver detalhes ▼'}</span>
        </button>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-white/5 p-3 text-xs">
            <div className="text-white/60">Total em NFs</div>
            <div className="mt-1 text-base font-semibold">{money(totalNfs)}</div>
            <div className="mt-1 text-white/40">{nfsFiltradas.length} nota{nfsFiltradas.length !== 1 ? 's' : ''}</div>
          </div>
          <div className="rounded-xl bg-white/5 p-3 text-xs">
            <div className="text-white/60">Produtos com NF</div>
            <div className="mt-1 text-base font-semibold text-emerald-400">{prodsComNf}</div>
            <div className="mt-1 text-white/40">{prodsSemNf} sem NF</div>
          </div>
        </div>

        {nfsFiltradas.length > 0 && (
          <div className="mt-2 rounded-xl bg-white/5 p-3 text-xs">
            <div className="text-white/60 mb-1">Última NF registrada</div>
            <div className="font-semibold">
              {nfsFiltradas[0].numero || '—'}
              {nfsFiltradas[0].fornecedorNome ? ` • ${nfsFiltradas[0].fornecedorNome}` : ''}
            </div>
            {nfsFiltradas[0].dataEmissao && (
              <div className="text-white/40">{new Date(nfsFiltradas[0].dataEmissao + 'T00:00:00').toLocaleDateString('pt-BR')}</div>
            )}
          </div>
        )}

        {showNfDetalhes && (
          <div className="mt-3 space-y-2">
            {fornecedoresMap.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-white/70 mb-2">Por Fornecedor</div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {fornecedoresMap.map(([nome, dados]) => (
                    <div key={nome} className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2 text-xs">
                      <div>
                        <div className="font-semibold truncate max-w-[160px]">{nome}</div>
                        <div className="text-white/50">{dados.qtd} NF{dados.qtd !== 1 ? 's' : ''}</div>
                      </div>
                      <div className="font-semibold text-right">{money(dados.total)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="text-xs font-semibold text-white/70 mb-2">Todas as Notas Fiscais</div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {nfsFiltradas.length === 0 && (
                  <div className="text-xs text-white/50">Nenhuma nota fiscal no período.</div>
                )}
                {nfsFiltradas.map(n => (
                  <div key={n.id} className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2 text-xs">
                    <div>
                      <div className="font-semibold">NF {n.numero || '—'}{n.serie ? ` / Série ${n.serie}` : ''}</div>
                      <div className="text-white/50">{n.fornecedorNome || 'Fornecedor não informado'}</div>
                      {n.dataEmissao && <div className="text-white/40">{new Date(n.dataEmissao + 'T00:00:00').toLocaleDateString('pt-BR')}</div>}
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{money(n.valorTotal)}</div>
                      {n.cnpjFornecedor && <div className="text-white/40">{n.cnpjFornecedor}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Gastos por Obra */}
      {gastosPorObra.length > 0 && (
        <div className="rounded-2xl cm-card p-4">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-sm">🏗️ Gastos por Obra</div>
            <div className="text-xs text-white/60">{gastosPorObra.length} obra{gastosPorObra.length > 1 ? 's' : ''}</div>
          </div>
          <div className="mt-3 space-y-2">
            {gastosPorObra.map((g) => (
              <div key={g.obraId} className="flex items-center justify-between rounded-xl bg-white/5 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{g.obraNome}</div>
                </div>
                <div className="ml-3 text-sm font-semibold text-emerald-400">{money(g.total)}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-2 border-t border-white/10 flex justify-between text-xs">
            <span className="text-white/60">Total em obras:</span>
            <span className="font-semibold">{money(sum(gastosPorObra.map(g => g.total)))}</span>
          </div>
        </div>
      )}

      <div className="rounded-2xl cm-card p-4 text-xs">
        <div className="font-semibold">Movimentações (resumo)</div>
        <div className="mt-2 max-h-64 overflow-y-auto divide-y divide-white/10">
          {filtered.slice(0,200).map(m => (
            <div key={m.id} className="py-2 flex items-center justify-between">
              <div className="min-w-0">
                <div className="truncate font-semibold">{m.produtoNome || '—'}</div>
                <div className="text-white/60">{m.tipo} • {new Date(m.criadoEm || m.dataMovimentacao || Date.now()).toLocaleDateString('pt-BR')}</div>
              </div>
              <div className="text-right">
                <div className="text-white/60">{m.classificacao || '—'}</div>
                <div className="font-semibold">{money(m.custoTotal)}</div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-white/60">Sem dados no período.</div>
          )}
        </div>
      </div>
    </div>
  )
}
